import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { api } from '../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Users, Briefcase, AlertTriangle, Calendar, Clock, Bell, CheckCircle } from 'lucide-react';

const Dashboard = () => {
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadDashboard();
  }, []);

  const loadDashboard = async () => {
    try {
      const response = await api.getDashboard();
      setStats(response.data);
    } catch (error) {
      console.error('Erro ao carregar dashboard:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleConcluirAnotacao = async (anotacaoId) => {
    try {
      await api.concluirAnotacao(anotacaoId);
      loadDashboard();
    } catch (error) {
      console.error('Erro ao concluir anotação:', error);
    }
  };

  if (loading) {
    return <div className="p-8" data-testid="dashboard-loading">Carregando...</div>;
  }

  return (
    <div className="p-4 sm:p-6 lg:p-8 space-y-6 lg:space-y-8" data-testid="dashboard-page">
      <div>
        <h1 className="text-2xl sm:text-3xl font-semibold text-slate-900">Dashboard</h1>
        <p className="text-slate-600 mt-1 text-sm sm:text-base">Visão geral do escritório</p>
      </div>

      <div className="grid grid-cols-2 gap-3 sm:gap-6">
        <Card data-testid="stat-card-clientes">
          <CardHeader className="flex flex-row items-center justify-between pb-2 p-3 sm:p-6">
            <CardTitle className="text-xs sm:text-sm font-medium text-slate-600">Total de Clientes</CardTitle>
            <Users className="w-4 h-4 sm:w-5 sm:h-5 text-slate-400" />
          </CardHeader>
          <CardContent className="p-3 sm:p-6 pt-0">
            <div className="text-2xl sm:text-3xl font-bold text-slate-900">{stats?.total_clientes || 0}</div>
          </CardContent>
        </Card>

        <Card data-testid="stat-card-processos">
          <CardHeader className="flex flex-row items-center justify-between pb-2 p-3 sm:p-6">
            <CardTitle className="text-xs sm:text-sm font-medium text-slate-600">Processos Ativos</CardTitle>
            <Briefcase className="w-4 h-4 sm:w-5 sm:h-5 text-slate-400" />
          </CardHeader>
          <CardContent className="p-3 sm:p-6 pt-0">
            <div className="text-2xl sm:text-3xl font-bold text-slate-900">{stats?.processos_ativos || 0}</div>
          </CardContent>
        </Card>
      </div>

      {/* Anotações do Dia */}
      {stats?.anotacoes_hoje && stats.anotacoes_hoje.length > 0 && (
        <Card className="border-2 border-blue-200 bg-blue-50" data-testid="anotacoes-hoje">
          <CardHeader className="p-3 sm:p-6">
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2">
              <div className="flex items-center gap-2">
                <Bell className="w-4 h-4 sm:w-5 sm:h-5 text-blue-600" />
                <CardTitle className="text-blue-900 text-sm sm:text-base">
                  Anotações de Hoje ({stats.anotacoes_hoje.length})
                </CardTitle>
              </div>
              <Button
                variant="outline"
                size="sm"
                onClick={() => navigate('/anotacoes')}
                className="w-full sm:w-auto"
              >
                Ver Todas
              </Button>
            </div>
          </CardHeader>
          <CardContent className="p-3 sm:p-6 pt-0">
            <div className="space-y-3">
              {stats.anotacoes_hoje.map((anotacao) => {
                const dataAlerta = new Date(anotacao.data_alerta);
                const prioridadeColor = anotacao.prioridade === 'Alta' ? 'bg-red-100 text-red-700 border-red-300' :
                                       anotacao.prioridade === 'Normal' ? 'bg-blue-100 text-blue-700 border-blue-300' :
                                       'bg-slate-100 text-slate-700 border-slate-300';
                
                return (
                  <div
                    key={anotacao.id}
                    className={`p-3 sm:p-4 rounded-lg border-2 ${prioridadeColor}`}
                    data-testid="anotacao-hoje-item"
                  >
                    <div className="flex flex-col sm:flex-row sm:justify-between sm:items-start gap-2">
                      <div className="flex-1">
                        <div className="flex flex-wrap items-center gap-2 mb-1">
                          <p className="font-semibold text-sm sm:text-base">{anotacao.titulo}</p>
                          <span className="text-xs px-2 py-0.5 bg-white/50 rounded">
                            {anotacao.prioridade}
                          </span>
                        </div>
                        {anotacao.descricao && (
                          <p className="text-xs sm:text-sm mt-1">{anotacao.descricao}</p>
                        )}
                        <div className="flex items-center gap-1 mt-2 text-xs">
                          <Clock className="w-3 h-3" />
                          {dataAlerta.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' })}
                        </div>
                      </div>
                      <Button
                        size="sm"
                        variant="outline"
                        className="bg-white hover:bg-green-50 w-full sm:w-auto"
                        onClick={() => handleConcluirAnotacao(anotacao.id)}
                      >
                        <CheckCircle className="w-4 h-4 mr-1 text-green-600" />
                        Concluir
                      </Button>
                    </div>
                  </div>
                );
              })}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Alertas de Audiências */}
      {stats?.audiencias_proximas && stats.audiencias_proximas.length > 0 && (
        <Card className="border-2 border-purple-200 bg-purple-50" data-testid="alertas-audiencias">
          <CardHeader className="p-3 sm:p-6">
            <div className="flex items-center gap-2">
              <Calendar className="w-4 h-4 sm:w-5 sm:h-5 text-purple-600" />
              <CardTitle className="text-purple-900 text-sm sm:text-base">
                Audiências Próximas ({stats.audiencias_proximas.length})
              </CardTitle>
            </div>
          </CardHeader>
          <CardContent className="p-3 sm:p-6 pt-0">
            <div className="space-y-3">
              {stats.audiencias_proximas.map((audiencia) => {
                const dataHora = new Date(audiencia.data_audiencia);
                const urgenciaColor = audiencia.urgencia === 'hoje' ? 'bg-red-100 text-red-700 border-red-300' : 
                                     audiencia.urgencia === 'proximo' ? 'bg-orange-100 text-orange-700 border-orange-300' : 
                                     'bg-purple-100 text-purple-700 border-purple-300';
                
                return (
                  <div
                    key={audiencia.id}
                    className={`p-3 sm:p-4 rounded-lg border-2 ${urgenciaColor}`}
                    data-testid="audiencia-alerta-item"
                  >
                    <div className="flex flex-col sm:flex-row sm:justify-between sm:items-start gap-3">
                      <div className="flex-1">
                        <div className="flex flex-wrap items-center gap-2 mb-1">
                          <p className="font-semibold text-sm sm:text-base">{audiencia.tipo_audiencia}</p>
                          {audiencia.urgencia === 'hoje' && (
                            <span className="text-xs px-2 py-0.5 bg-red-200 rounded font-bold">HOJE</span>
                          )}
                        </div>
                        <p className="text-xs sm:text-sm mt-1">{audiencia.processo?.numero_processo}</p>
                        <p className="text-xs sm:text-sm">Cliente: {audiencia.cliente?.nome_completo}</p>
                        <div className="flex flex-wrap items-center gap-2 sm:gap-4 mt-2 text-xs">
                          <div className="flex items-center gap-1">
                            <Calendar className="w-3 h-3" />
                            {dataHora.toLocaleDateString('pt-BR')}
                          </div>
                          <div className="flex items-center gap-1">
                            <Clock className="w-3 h-3" />
                            {dataHora.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' })}
                          </div>
                        </div>
                        {audiencia.local && (
                          <p className="text-xs mt-1">Local: {audiencia.local}</p>
                        )}
                      </div>
                      <div className="flex sm:flex-col items-center sm:items-end gap-2">
                        <div className="text-xl sm:text-2xl font-bold">
                          {audiencia.tempo_restante}
                        </div>
                        <Button
                          size="sm"
                          variant="outline"
                          className="flex-1 sm:flex-none"
                          onClick={() => navigate(`/processos/${audiencia.processo_id}`)}
                        >
                          Ver Processo
                        </Button>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Alertas de Prazos */}
      {stats?.prazos_proximos && stats.prazos_proximos.length > 0 && (
        <Card className="border-2 border-amber-200 bg-amber-50" data-testid="alertas-prazos">
          <CardHeader className="p-3 sm:p-6">
            <div className="flex items-center gap-2">
              <AlertTriangle className="w-4 h-4 sm:w-5 sm:h-5 text-amber-600" />
              <CardTitle className="text-amber-900 text-sm sm:text-base">
                Atenção! Prazos Próximos ({stats.prazos_proximos.length})
              </CardTitle>
            </div>
          </CardHeader>
          <CardContent className="p-3 sm:p-6 pt-0">
            <div className="space-y-3">
              {stats.prazos_proximos.map((processo) => {
                const prazoColor = processo.dias_restantes <= 3 ? 'bg-red-100 text-red-700 border-red-300' :
                                  processo.dias_restantes <= 7 ? 'bg-amber-100 text-amber-700 border-amber-300' :
                                  'bg-blue-100 text-blue-700 border-blue-300';
                
                return (
                  <div
                    key={processo.id}
                    className={`p-3 sm:p-4 rounded-lg border-2 ${prazoColor}`}
                    data-testid="prazo-alerta-item"
                  >
                    <div className="flex flex-col sm:flex-row sm:justify-between sm:items-start gap-3">
                      <div className="flex-1">
                        <p className="font-semibold text-sm sm:text-base">{processo.numero_processo}</p>
                        <p className="text-xs sm:text-sm mt-1">{processo.tipo_acao}</p>
                        <p className="text-xs sm:text-sm">Cliente: {processo.cliente?.nome_completo}</p>
                        <p className="text-xs mt-2">
                          Data Limite: {new Date(processo.data_limite).toLocaleDateString('pt-BR')}
                        </p>
                      </div>
                      <div className="flex sm:flex-col items-center sm:items-end gap-2">
                        <div className="text-xl sm:text-2xl font-bold">
                          {processo.dias_restantes === 0 ? 'HOJE' : `${processo.dias_restantes} ${processo.dias_restantes === 1 ? 'dia' : 'dias'}`}
                        </div>
                        <Button
                          size="sm"
                          variant="outline"
                          className="flex-1 sm:flex-none"
                          onClick={() => navigate(`/processos/${processo.id}`)}
                        >
                          Ver Processo
                        </Button>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </CardContent>
        </Card>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card>
          <CardHeader>
            <CardTitle>Processos Recentes</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              {stats?.processos_recentes?.length > 0 ? (
                stats.processos_recentes.map((processo) => (
                  <div key={processo.id} className="flex justify-between items-start p-3 bg-slate-50 rounded-lg" data-testid="processo-recente-item">
                    <div>
                      <p className="font-medium text-slate-900">{processo.numero_processo}</p>
                      <p className="text-sm text-slate-600">{processo.cliente?.nome_completo}</p>
                    </div>
                    <span className="text-xs px-2 py-1 bg-blue-100 text-blue-700 rounded">{processo.status}</span>
                  </div>
                ))
              ) : (
                <p className="text-slate-500 text-sm">Nenhum processo encontrado</p>
              )}
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Movimentações Recentes</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              {stats?.movimentacoes_recentes?.length > 0 ? (
                stats.movimentacoes_recentes.map((mov) => (
                  <div key={mov.id} className="p-3 bg-slate-50 rounded-lg" data-testid="movimentacao-recente-item">
                    <p className="font-medium text-slate-900 text-sm">{mov.processo?.numero_processo}</p>
                    <p className="text-sm text-slate-600 mt-1">{mov.descricao}</p>
                    <p className="text-xs text-slate-500 mt-1">{new Date(mov.data_movimentacao).toLocaleDateString('pt-BR')}</p>
                  </div>
                ))
              ) : (
                <p className="text-slate-500 text-sm">Nenhuma movimentação encontrada</p>
              )}
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default Dashboard;